export type Resolve<T> = (value: T) => void;
export type Reject<E = unknown> = (reason?: E) => void;

/**
 * Creates a "deferred" object that can be resolved later by the same code that created the promise.
 * "Deferred" is syntactic sugar for saving a promise's `resolve` function onto a closed scope
 * (in order to manually invoke it later), but our compiler does not treat this pattern nicely.
 *
 * This replicates the basic Deferred functionality of the Q library, but uses an ES6 promise,
 * thus avoiding us having to take a dependency on Q.
 */
export class SimpleDeferred<T, E = unknown> {
  public readonly promise: Promise<T>;

  private internalResolve!: Resolve<T>;
  private internalReject!: Reject<E>;

  public constructor() {
    this.promise = new Promise<T>((resolve, reject) => {
      this.internalResolve = resolve;
      this.internalReject = reject;
    });
  }

  public get resolve(): Resolve<T> {
    return this.internalResolve;
  }

  public get reject(): Reject<E> {
    return this.internalReject;
  }
}
